import { useMemo, useState } from 'react';
import {
  DndContext, DragEndEvent, DragOverlay, DragStartEvent,
  PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { api } from '@/lib/api';
import { DemandaCard } from './DemandaCard';
import { KanbanColumn } from './KanbanColumn';
import type { Demanda, Solicitante, Area, StatusDemanda } from '@/lib/types';

interface Props {
  demandas: Demanda[];
  solicitantes: Solicitante[];
  areas: Area[];
  onCardClick?: (id: string) => void;
}

// Pipeline ativo apenas. Publicada vira card-resumo clicavel pra /entregas.
const COLUMNS: { status: StatusDemanda; color: 'blue' | 'orange' | 'purple' | 'pink' }[] = [
  { status: 'Triagem', color: 'blue' },
  { status: 'Em produção', color: 'orange' },
  { status: 'Em revisão', color: 'purple' },
  { status: 'Aguardando aprovação', color: 'pink' },
];

const PAGE_SIZE = 20;

export function Kanban({ demandas, solicitantes, areas, onCardClick }: Props) {
  const qc = useQueryClient();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<StatusDemanda>>(new Set());

  const solicitanteMap = useMemo(
    () => new Map(solicitantes.map((s) => [s.id, s.nome])),
    [solicitantes],
  );
  const areaMap = useMemo(
    () => new Map(areas.map((a) => [a.id, a.nome])),
    [areas],
  );

  const byStatus = useMemo(() => {
    const m = new Map<StatusDemanda, Demanda[]>();
    COLUMNS.forEach((c) => m.set(c.status, []));
    demandas.forEach((d) => {
      const list = m.get(d.status);
      if (list) list.push(d);
    });
    const ordemPrio: Record<string, number> = { 'Crítica': 0, Alta: 1, 'Média': 2, Baixa: 3 };
    m.forEach((list) => {
      list.sort((a, b) => {
        const pa = ordemPrio[a.prioridade] ?? 9;
        const pb = ordemPrio[b.prioridade] ?? 9;
        if (pa !== pb) return pa - pb;
        if (a.prazoSla && b.prazoSla) return a.prazoSla.localeCompare(b.prazoSla);
        if (a.prazoSla) return -1;
        if (b.prazoSla) return 1;
        return a.titulo.localeCompare(b.titulo);
      });
    });
    return m;
  }, [demandas]);

  const totalPublicadas = demandas.filter((d) => d.status === 'Publicada').length;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const mut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: StatusDemanda }) =>
      api.demandas.update(id, { status }).then((r) => r.data),
    onMutate: async ({ id, status }) => {
      await qc.cancelQueries({ queryKey: ['demandas'] });
      const prev = qc.getQueryData<Demanda[]>(['demandas']);
      qc.setQueryData<Demanda[]>(['demandas'], (old) =>
        old?.map((d) => (d.id === id ? { ...d, status } : d)) ?? [],
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(['demandas'], ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['demandas'] }),
  });

  function handleDragStart(e: DragStartEvent) { setActiveId(String(e.active.id)); }
  function handleDragEnd(e: DragEndEvent) {
    setActiveId(null);
    const { active, over } = e;
    if (!over) return;
    const id = String(active.id);
    const targetStatus = over.id as StatusDemanda;
    const currentStatus = (active.data.current?.status as StatusDemanda) ?? undefined;
    if (!targetStatus || currentStatus === targetStatus) return;
    mut.mutate({ id, status: targetStatus });
  }

  const activeDemanda = activeId ? demandas.find((d) => d.id === activeId) : null;

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-2 min-h-[500px]">
        {COLUMNS.map(({ status, color }) => {
          const list = byStatus.get(status) ?? [];
          const isExpanded = expanded.has(status);
          const visible = isExpanded ? list : list.slice(0, PAGE_SIZE);
          const hasMore = list.length > PAGE_SIZE && !isExpanded;
          return (
            <KanbanColumn key={status} status={status} count={list.length} color={color}>
              {visible.map((d) => (
                <DemandaCard
                  key={d.id}
                  demanda={d}
                  solicitanteNome={solicitanteMap.get(d.solicitanteId)}
                  areasNomes={d.areasIds.map((id) => areaMap.get(id)).filter(Boolean) as string[]}
                  onClick={onCardClick ? () => onCardClick(d.id) : undefined}
                />
              ))}
              {hasMore && (
                <button type="button"
                  onClick={() => setExpanded(prev => new Set([...prev, status]))}
                  className="w-full text-center text-[10px] text-blue-light hover:text-blue py-2 font-semibold">
                  + {list.length - PAGE_SIZE} mais
                </button>
              )}
            </KanbanColumn>
          );
        })}

        {/* Card-resumo Publicada → leva pra /entregas */}
        <Link
          to="/entregas"
          className="bg-panel border border-border rounded-xl p-3 flex flex-col items-center justify-center text-center hover:border-green/50 hover:bg-green/5 transition-colors min-h-[200px]"
        >
          <CheckCircle2 className="w-7 h-7 text-green mb-2" />
          <div className="text-[10px] font-bold uppercase tracking-wider text-text-2 mb-1">Finalizadas</div>
          <div className="text-2xl font-black text-text">{totalPublicadas}</div>
          <div className="text-[11px] text-muted mt-2 px-2">ver vitrine consolidada por cliente</div>
        </Link>
      </div>

      <DragOverlay>
        {activeDemanda && (
          <DemandaCard
            demanda={activeDemanda}
            solicitanteNome={solicitanteMap.get(activeDemanda.solicitanteId)}
            areasNomes={activeDemanda.areasIds.map((id) => areaMap.get(id)).filter(Boolean) as string[]}
          />
        )}
      </DragOverlay>
    </DndContext>
  );
}
